import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { snackbar, LinkItem } from '@occc-app/design-system';
import { useCreateKioskDefaultScreenMutation } from '@occc/graphql';

import { DefaultScreensCreatePage } from './default-screens-create-page.component';
import { DefaultScreenFormValues } from '../../touchpoint.types';

import { routeCreator } from '../../touchpoint.router';
import {
  DEFAULT_SCREEN_CREATE_SUCCESS_MESSAGE,
  DEFAULT_SCREEN_CREATE_ERROR_MESSAGE,
} from '../../touchpoint.constants';

export const DefaultScreensCreatePageContainer = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [createKioskDefaultScreen] = useCreateKioskDefaultScreenMutation();

  const breadcrumbLinks: LinkItem[] = useMemo(
    () => [
      {
        to: routeCreator.defaultScreensList(),
        label: 'Touchpoint',
      },
      {
        key: routeCreator.defaultScreensCreate(),
        label: 'Default Screens',
      },
    ],
    [],
  );

  const handleCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onSubmit = useCallback(
    async (values: DefaultScreenFormValues) => {
      setIsSubmitting(true);

      try {
        const imageSet: {
          landscape?: string;
          square?: string;
          portrait?: string;
        } = {};

        if (values.landscapeImageUrl) {
          imageSet.landscape = values.landscapeImageUrl;
        }
        if (values.squareImageUrl) {
          imageSet.square = values.squareImageUrl;
        }
        if (values.portraitImageUrl) {
          imageSet.portrait = values.portraitImageUrl;
        }

        const { data } = await createKioskDefaultScreen({
          variables: {
            input: {
              name: values.name.trim(),
              description: values.description.trim(),
              imageSet,
              kioskIds: values.kioskIds || [],
            },
          },
          refetchQueries: ['kioskDefaultScreens'],
        });

        if (data?.createKioskDefaultScreen) {
          snackbar.success(DEFAULT_SCREEN_CREATE_SUCCESS_MESSAGE);
          navigate(routeCreator.defaultScreensList());
        } else {
          snackbar.error(DEFAULT_SCREEN_CREATE_ERROR_MESSAGE);
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error('Error creating default screen:', error);
        snackbar.error(DEFAULT_SCREEN_CREATE_ERROR_MESSAGE);
        setIsSubmitting(false);
      }
    },
    [createKioskDefaultScreen, navigate],
  );

  return (
    <DefaultScreensCreatePage
      breadcrumbLinks={breadcrumbLinks}
      onSubmit={onSubmit}
      onCancel={handleCancel}
      isSubmitting={isSubmitting}
    />
  );
};

